import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeftIcon, ChevronDownIcon, LockIcon } from 'lucide-react';
import { useCheckout, stepOrder } from '../hooks/useCheckout';
import { CheckoutSection, type SectionState } from '../components/checkout/CheckoutSection';
import { ContactStep } from '../components/checkout/ContactStep';
import { DeliveryStep } from '../components/checkout/DeliveryStep';
import { OrderSummary } from '../components/checkout/OrderSummary';
import { PaymentStep } from '../components/checkout/PaymentStep';
import { btnPrimary } from '../components/ui/buttonStyles';
import { formatPrice } from '../utils/currency';
import { easeOut } from '../utils/motion';
import type { CheckoutStep } from '../utils/checkout';
export function Checkout() {
  const checkout = useCheckout();
  const [summaryOpen, setSummaryOpen] = useState(false);
  const {
    step,
    completed,
    form,
    shipping,
    lines,
    status,
    total
  } = checkout;
  const stateOf = (s: CheckoutStep): SectionState => {
    if (s === step) return 'active';
    if (completed.includes(s)) return 'done';
    return 'locked';
  };
  const shell = (children: React.ReactNode) => <div className="min-h-screen w-full bg-paper text-ink" data-id="element-675" data-mp="cxye23dpjd6v">
      <header className="border-b border-line" data-id="element-676" data-mp="hx186ss5fz02">
        <div className="mx-auto grid h-16 max-w-6xl grid-cols-[1fr_auto_1fr] items-center px-5 md:h-20 md:px-8" data-id="element-677" data-mp="hvq40ruskp8f">
          <Link to="/panier" className="flex items-center gap-2 text-sm text-muted hover:text-ink" data-id="element-678" data-mp="3gg9fsb7cbf80">
            <ArrowLeftIcon className="h-4 w-4" data-id="element-679" data-mp="3lf2xygxknuei" /> <span className="hidden sm:inline" data-id="element-680" data-mp="3qbt9qs2cg3u4">Retour au panier</span>
          </Link>
          <Link to="/" className="whitespace-nowrap font-display text-[22px] md:text-[28px]" data-id="element-681" data-mp="3udoek6jhgzcn">
            L’Éphémère <em className="text-clay" data-id="element-682" data-mp="15mn519l1u24w">Perpétuel</em>
          </Link>
          <span className="flex items-center justify-end gap-1.5 text-xs text-muted" data-id="element-683" data-mp="15oge0vj4wyg2">
            <LockIcon className="h-3.5 w-3.5" data-id="element-684" data-mp="19lw2aofasi57" /> <span className="hidden sm:inline" data-id="element-685" data-mp="36o36p12d8uxo">Paiement sécurisé</span>
          </span>
        </div>
      </header>
      {children}
    </div>;
  if (lines.length === 0 && status !== 'processing') {
    return shell(<main className="mx-auto max-w-xl px-5 py-24 text-center" data-id="element-686" data-mp="3fp9mt913s3qe">
        <h1 className="font-display text-4xl" data-id="element-687" data-mp="3apx32x5j3cs1">Votre panier est vide</h1>
        <p className="mt-3 text-muted" data-id="element-688" data-mp="3aqq5jlxgfg4d">Ajoutez quelques pièces avant de passer commande.</p>
        <Link to="/boutique" className={`${btnPrimary} mt-8`} data-id="element-689" data-mp="2n0lzl7wsn4a7">
          Retour à la boutique
        </Link>
      </main>);
  }
  return shell(<main className="mx-auto max-w-6xl px-5 pb-20 md:px-8" data-id="element-690" data-mp="2wqp1xypt1ul7">
      <div className="-mx-5 border-b border-line bg-card px-5 lg:hidden" data-id="element-691" data-mp="2ws8i7pbgo5mz">
        <button type="button" onClick={() => setSummaryOpen(o => !o)} aria-expanded={summaryOpen} className="flex w-full items-center justify-between py-4 text-sm" data-id="element-692" data-mp="2rynklyds5dob">
          <span className="flex items-center gap-2" data-id="element-693" data-mp="1zahyb772vgmd">
            {summaryOpen ? 'Masquer' : 'Afficher'} le récapitulatif
            <ChevronDownIcon className={`h-4 w-4 transition-transform duration-200 ease-out ${summaryOpen ? 'rotate-180' : ''}`} data-id="element-694" data-mp="eqwcxj7scqvo" />
          </span>
          <span className="font-medium tabular-nums" data-id="element-695" data-mp="5t3tc2v16x2h">{formatPrice(total)}</span>
        </button>
        <AnimatePresence initial={false} data-id="element-696" data-mp="as6yroexb5az">
          {summaryOpen && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.28,
          ease: easeOut
        }} className="overflow-hidden" data-id="element-697" data-mp="ygzqt9sfct0b">
              <div className="pb-6 pt-2" data-id="element-698" data-mp="thda2qde0umr">
                <OrderSummary checkout={checkout} data-id="element-699" data-mp="tgbwof8wfw1v" />
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>

      <div className="grid gap-12 lg:grid-cols-[1fr_380px] lg:gap-16" data-id="element-700" data-mp="3e7ukie7oue5q">
        <div data-id="element-701" data-mp="3e95puigs7tyy">
          <h1 className="pt-8 font-display text-4xl md:pt-12 md:text-5xl" data-id="element-702" data-mp="3e5ht7ag1irg0">Finaliser la commande</h1>
          <CheckoutSection index={1} title="Coordonnées" state={stateOf('contact')} onEdit={() => checkout.editStep('contact')} summary={<>
                {form.firstName} {form.lastName} · {form.email}
              </>} data-id="element-703" data-mp="3tjc41lwgn0yz">
            <ContactStep checkout={checkout} data-id="element-704" data-mp="gaph8er0i93q" />
          </CheckoutSection>
          <CheckoutSection index={2} title="Livraison" state={stateOf('delivery')} onEdit={() => checkout.editStep('delivery')} summary={<>
                {shipping.label}
                {shipping.id !== 'pickup' && ` · ${form.address}, ${form.postalCode} ${form.city}`}
                {form.giftWrap && ' · Emballage cadeau'}
              </>} data-id="element-705" data-mp="g8muq4p2iy3r">
            <DeliveryStep checkout={checkout} data-id="element-706" data-mp="2n0dufa3mmjfw" />
          </CheckoutSection>
          <CheckoutSection index={3} title="Paiement" state={stateOf('payment')} data-id="element-707" data-mp="2cxqlzy8l3ql5">
            <PaymentStep checkout={checkout} data-id="element-708" data-mp="2hwl6d4m79n7d" />
          </CheckoutSection>
          <p className="mt-6 text-xs text-muted" data-id="element-709" data-mp="2hwuwqtb5pwdl">
            Étape {stepOrder.indexOf(step) + 1} sur {stepOrder.length}. Vos informations sont conservées si vous revenez en arrière.
          </p>
        </div>
        <aside className="hidden self-start pt-12 lg:sticky lg:top-8 lg:block" aria-label="Récapitulatif de commande" data-id="element-710" data-mp="2rohdt6a6epo9">
          <div className="rounded-md bg-card p-6" data-id="element-711" data-mp="30n2yb6z7kero">
            <h2 className="mb-5 font-display text-2xl" data-id="element-712" data-mp="30lhzv69rwboj">Récapitulatif</h2>
            <OrderSummary checkout={checkout} data-id="element-713" data-mp="35lwbeyal23rb" />
          </div>
        </aside>
      </div>
    </main>);
}